/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jan 15 2024
 **/

import React, { ForwardedRef, forwardRef, useContext } from "react";

import { Button, Icon, Row } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";
import { colors } from "@/frameworks/core/styles";

import * as S from "../emotion";
import { useGuestSignupConfirm, usePopover } from "@/frameworks/core/hooks";
import { POPOVER_ID } from "@/frameworks/core/commons";
import EmoticonPicker from "../../EmoticonPickers/EmoticonPicker";
import ChatInputContext from "./ChatInputContext";
import { Properties } from "@/frameworks/libraries";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOGTAG = "#ChatButton#";

function ChatButtonComponent(
	{
		update,
		anchorElement,
		disabled,
		onSelectEmoticon,
		onCancel,
		onSubmit,
		onUploadFile,
		onClickSiren,
		onClickVote,
		isEmergency,
		isHideSiren,
		isHideVote,
		variant
	}: {
		update?: boolean;
		anchorElement: HTMLElement | null;
		disabled: boolean;
		onSelectEmoticon: (emoji: { name: string; path?: string }) => void;
		onUploadFile: (ev: React.ChangeEvent<HTMLInputElement>) => void;
		onClickSiren: () => void;
		onClickVote: () => void;
		onCancel?: () => void;
		onSubmit?: () => void;
		isHideSiren?: boolean;
		isHideVote?: boolean;
		variant?: ChatUI.Size;
		isEmergency?: boolean;
	},
	ref: ForwardedRef<HTMLButtonElement>
) {
	const popover = usePopover();
	const chatInputContext = useContext(ChatInputContext);
	const isGuest = Properties.property.isGuest;
	const guestSignupConfirm = useGuestSignupConfirm();

	const showEmoticon = () => {
		if (isGuest) {
			guestSignupConfirm(() => {});
			return;
		}
		popover.show({
			popoverID: POPOVER_ID.CHAT_EMOTICON_PICKER,
			content: (
				<EmoticonPicker
					onSelectEmoticon={(emoticon) => {
						onSelectEmoticon(emoticon);
						popover.hide(POPOVER_ID.CHAT_EMOTICON_PICKER);
					}}
				/>
			),
			anchorEl: anchorElement,
			direction: {
				x: "left",
				y: "top"
			},
			position: {
				x: 0,
				y: -288
			}
		});
		chatInputContext?.editor?.focus();
	};

	const clickVote = () => {
		if (isGuest) {
			guestSignupConfirm(() => {});
			return;
		}
		onClickVote();
	};

	const clickSiren = () => {
		onClickSiren();
		chatInputContext?.editor?.focus();
	};

	const uploadFiles = (ev: React.ChangeEvent<HTMLInputElement>) => {
		onUploadFile(ev);
		chatInputContext?.editor?.focus();
	};

	return (
		<S.IconArea horizonAlign="distribute" verticalAlign="center" variant={variant}>
			<Row gap={8}>
				<Maybe test={!update}>
					<Button.Icon type="button" name="Emoji24" variant="text" size="medium" onClick={showEmoticon} />
					<S.IconLabel>
						<Icon
							name="Upload24"
							size={20}
							onClick={(event) => {
								if (isGuest) {
									event.stopPropagation();
									event.preventDefault();
									guestSignupConfirm(() => {});
								}
							}}
						/>
						<input id="upload" type="file" css={{ display: "none" }} onChange={uploadFiles} multiple />
					</S.IconLabel>
					<Maybe test={!isHideVote}>
						<Button.Icon type="button" name="Vote24" variant="text" size="medium" onClick={clickVote} />
					</Maybe>
					{!isHideSiren && (
						<Row
							css={{
								backgroundColor: isEmergency ? colors.warning_00_100 : "transparent",
								borderRadius: 20
							}}
						>
							<Button.Icon
								type="button"
								name="Siren24"
								variant="text"
								size="medium"
								onClick={clickSiren}
							/>
						</Row>
					)}
				</Maybe>
			</Row>
			<Row gap={4}>
				<Maybe
					test={!update}
					fallback={
						<>
							<Button
								variant="contained"
								filled="secondary"
								onClick={() => {
									onCancel?.();
								}}
							>
								{_T("취소")}
							</Button>
							<Button disabled={disabled} variant="contained" onClick={onSubmit} ref={ref}>
								{_T("저장")}
							</Button>
						</>
					}
				>
					<Button.Icon
						type="button"
						name="Send24"
						variant="contained"
						size="medium"
						filled="transparent"
						onClick={onSubmit}
						disabled={disabled || isGuest}
						ref={ref}
					/>
				</Maybe>
			</Row>
		</S.IconArea>
	);
}

const ChatButtons = forwardRef(ChatButtonComponent);
ChatButtons.displayName = "ChatButtons";
export default ChatButtons;
